import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import careerService, { 
  CareerInquiry, 
  CareerAnalysisRequest, 
  CareerInquiryCreateRequest,
  CareerInquiriesListResponse 
} from '@/services/careerService';
import simpleCareerService from '@/services/careerServiceSimple';
import ToastService from '@/utils/toast';

export const useCareerData = () => {
  const queryClient = useQueryClient();

  // Fetch user career inquiries
  const {
    data: inquiriesData,
    isLoading,
    error,
    refetch
  } = useQuery<CareerInquiriesListResponse>({
    queryKey: ['career', 'inquiries'],
    queryFn: () => careerService.getUserCareerInquiries(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Analyze career inquiry mutation
  const analyzeCareerMutation = useMutation({
    mutationFn: (request: CareerAnalysisRequest) => 
      careerService.analyzeCareerInquiry(request),
    onError: (error) => {
      console.error('Career analysis failed:', error);
      ToastService.showError('Failed to analyze career inquiry. Please try again.');
    }
  });

  // Save career inquiry mutation
  const saveCareerInquiryMutation = useMutation({
    mutationFn: (inquiryData: CareerInquiryCreateRequest) => 
      careerService.saveCareerInquiry(inquiryData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['career', 'inquiries'] });
      ToastService.showSuccess('Career exploration saved successfully!');
    },
    onError: (error) => {
      console.error('Failed to save career inquiry:', error);
      ToastService.showError('Failed to save career exploration. Please try again.');
    }
  });

  // Delete career inquiry mutation
  const deleteCareerInquiryMutation = useMutation({
    mutationFn: (inquiryId: number) => 
      careerService.deleteCareerInquiry(inquiryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['career', 'inquiries'] });
      ToastService.showSuccess('Career exploration deleted successfully!');
    },
    onError: (error) => {
      console.error('Failed to delete career inquiry:', error);
      ToastService.showError('Failed to delete career exploration. Please try again.');
    }
  });

  return {
    // Data
    inquiries: inquiriesData?.inquiries || [],
    totalCount: inquiriesData?.total_count || 0,
    
    // Loading states
    isLoading,
    isAnalyzing: analyzeCareerMutation.isPending,
    isSaving: saveCareerInquiryMutation.isPending,
    isDeleting: deleteCareerInquiryMutation.isPending,
    
    // Error states
    error,
    
    // Actions
    analyzeCareer: analyzeCareerMutation.mutateAsync,
    saveCareerInquiry: saveCareerInquiryMutation.mutateAsync,
    deleteCareerInquiry: deleteCareerInquiryMutation.mutateAsync,
    refetch,
    
    // Analysis result
    analysisResult: analyzeCareerMutation.data,
    analysisError: analyzeCareerMutation.error
  };
};